<template>
  <div class="media-carousel">
    <el-carousel
      v-if="props.mediaList.length"
      ref="carouselRef"
      :arrow="playingIndex !== -1 ? 'never' : 'always'"
      :autoplay="false"
      height="100%"
      indicator-position="none"
      :loop="props.mediaList.length > 1"
      @change="handleChange"
    >
      <el-carousel-item v-for="(item, index) in props.mediaList" :key="`${item.filepath}-${index}`">
        <div class="media-carousel__item">
          <img
            v-if="getMediaType(item) === 'image'"
            alt=""
            class="media-carousel__image"
            :src="item.filepath"
          />

          <div v-else class="media-carousel__video">
            <video
              :ref="(el) => setVideoRef(el, index)"
              class="media-carousel__video-player"
              :controls="playingIndex === index"
              controlslist="nodownload"
              preload="metadata"
              :src="item.filepath"
              @ended="handleVideoStop(index)"
              @pause="handleVideoStop(index, $event)"
              @play="handleVideoPlay(index)"
            ></video>
            <div v-show="playingIndex !== index" class="media-carousel__video-mask"></div>
            <button
              v-show="playingIndex !== index"
              class="media-carousel__video-button"
              type="button"
              @click="playVideo(index)"
            >
              <i class="icon-play-fill t-iconfont"></i>
            </button>
          </div>
        </div>
      </el-carousel-item>
    </el-carousel>

    <div
      v-if="props.mediaList.length > 1"
      v-show="playingIndex === -1"
      class="media-carousel__indicator"
    >
      {{ activeIndex + 1 }}/{{ props.mediaList.length }}
    </div>
  </div>
</template>

<script setup>
import { ref, watch } from 'vue'

defineOptions({
  name: 'MediaCarousel'
})

const props = defineProps({
  mediaList: {
    type: Array,
    default: () => []
  }
})

const carouselRef = ref(null)
const videoRefs = ref([])
const playingIndex = ref(-1)
const activeIndex = ref(0)

const getMediaType = (item) => {
  const ext = String(item?.ext || '').toLowerCase()
  if (['mp4', 'webm', 'ogg', 'mov'].includes(ext)) return 'video'
  return 'image'
}

watch(
  () => props.mediaList,
  () => {
    videoRefs.value.forEach((video) => {
      video?.pause?.()
    })
    videoRefs.value = []
    playingIndex.value = -1
    activeIndex.value = 0
    carouselRef.value?.setActiveItem?.(0)
  },
  { immediate: true }
)

const setVideoRef = (el, index) => {
  if (el) {
    videoRefs.value[index] = el
  }
}

const pauseVideo = (index) => {
  videoRefs.value[index]?.pause?.()
}

const handleChange = (current) => {
  activeIndex.value = current
  if (playingIndex.value !== -1) {
    pauseVideo(playingIndex.value)
  }
  playingIndex.value = -1
}

const playVideo = async (index) => {
  const video = videoRefs.value[index]
  if (!video) return

  try {
    await video.play()
  } catch (error) {
    // ignore autoplay restrictions
  }
}

const handleVideoPlay = (index) => {
  playingIndex.value = index
}

const handleVideoStop = (index, event) => {
  if (event?.target?.seeking) {
    return
  }

  if (playingIndex.value === index) {
    playingIndex.value = -1
  }
}
</script>

<style lang="scss" scoped>
:deep(.el-carousel__arrow) {
  --el-carousel-arrow-background: rgba(0, 0, 0, 0.3);
}
.media-carousel {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  background: #ffffff;

  :deep(.el-carousel) {
    width: 100%;
    height: 100%;
  }

  :deep(.el-carousel__container) {
    height: 100%;
  }

  :deep(.el-carousel__item) {
    display: flex;
    align-items: stretch;
    justify-content: stretch;
  }

  &__item {
    width: 100%;
    height: 100%;
  }

  &__indicator {
    position: absolute;
    left: 50%;
    bottom: 12px;
    z-index: 2;
    min-width: 38px;
    height: 20px;
    padding: 2px 8px;
    box-sizing: border-box;
    border-radius: 64px;
    background: rgba(0, 0, 0, 0.3);
    font-family: 'PingFang SC', sans-serif;
    font-size: 12px;

    line-height: 16px;
    color: #ffffff;
    text-align: center;
    transform: translateX(-50%);
    pointer-events: none;
  }

  &__image,
  &__video-player {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  &__video {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__video-mask {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, #0000001a 0%, #00000080 100%);
  }

  &__video-button {
    position: absolute;
    left: 50%;
    top: 50%;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(12px);
    cursor: pointer;

    .t-iconfont {
      border-radius: 3px;
      font-size: 38px;
      background: linear-gradient(326deg, #1574ff 33%, #5952ff 63%, #a969fe 91%);
      color: transparent;
      background-clip: text;
    }
  }
}
</style>
